import React from 'react';
import { Button, LabeledInput, AlertNotification } from "@commonsku/styles";
import {oauth} from '../../utils';

type TaxMode = 'zip2tax' | 'manual';

type ProjectTaxStatus = {
  tenant_domain_key: string;
  tenant_name: string;
  job_number: string | number;
  job_name?: string;
  using_zip2tax: boolean;
  total_orders: number;
  zip2tax_orders: number;
  manual_orders: number;
  tax_mode: TaxMode;
  message: string;
};

const taxModeLabel = (mode: TaxMode) =>
  mode === 'zip2tax' ? 'Zip2Tax' : 'Manual taxes';

const isTaxModeOutOfSync = (project: ProjectTaxStatus): boolean =>
  project.using_zip2tax
    ? project.manual_orders > 0
    : project.zip2tax_orders > 0;

const getOppositeTaxMode = (mode: TaxMode): TaxMode =>
  mode === 'zip2tax' ? 'manual' : 'zip2tax';

const ProjectTaxMode = () => {
  const [error, setError] = React.useState('');
  const [success, setSuccess] = React.useState('');
  const [info, setInfo] = React.useState('');
  const [project, setProject] = React.useState<ProjectTaxStatus | null>(null);
  const [canSwitch, setCanSwitch] = React.useState(false);

  const clearMessages = () => {
    setError('');
    setSuccess('');
    setInfo('');
  };

  const handleError = (err: any, fallback: string) => {
    if (err.json?.error && typeof err.json.error === 'string') {
      setError(err.json.error);
    } else if (err.response?.status === 404) {
      setError(fallback);
    } else if (err.response?.status === 403) {
      setError('Access denied. You do not have permission to perform this action.');
    } else if (err.response?.status === 400) {
      setError('Invalid request. Please check your input and try again.');
    } else {
      setError('An error occurred while processing your request. Please try again.');
    }
  };

  const handleCheck = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    clearMessages();
    setProject(null);
    setCanSwitch(false);

    try {
      const formData = Object.fromEntries(new FormData(e.currentTarget).entries());
      const response = await oauth('POST', 'tech-support', {
        ...formData,
        actionName: 'check_project_tax_mode',
      });
      const data = response.json as ProjectTaxStatus;
      setProject(data);
      setCanSwitch(true);
      setInfo(data.message);
    } catch (err) {
      handleError(
        err,
        'Project or tenant not found. Please check the tenant domain key and project number.'
      );
    }
  };

  const handleSwitch = async (targetMode: TaxMode) => {
    if (!project) {
      return;
    }

    const orderCount = project.total_orders;
    const orderLabel = 1 === orderCount ? 'order' : 'orders';
    const confirmed = window.confirm(
      `Switch project #${project.job_number} to ${taxModeLabel(targetMode)}?\n\nThis will update the project and its ${orderCount} ${orderLabel}.`
    );
    if (!confirmed) {
      return;
    }

    clearMessages();
    try {
      const response = await oauth('POST', 'tech-support', {
        tenant_domain_key: project.tenant_domain_key,
        job_number: project.job_number,
        target_tax_mode: targetMode,
        actionName: 'switch_project_tax_mode',
      });
      const data = response.json as ProjectTaxStatus;
      setProject(data);
      setCanSwitch(false);
      setSuccess(data.message);
    } catch (err) {
      handleError(
        err,
        'Unable to switch this project tax mode. Please check the details and try again.'
      );
    }
  };

  const outOfSync = project ? isTaxModeOutOfSync(project) : false;
  const oppositeMode = project ? getOppositeTaxMode(project.tax_mode) : 'manual';

  return (
    <div>
      <form action="" onSubmit={handleCheck}>
        <LabeledInput
          label="Tenant Domain Key"
          placeholder="Enter tenant domain key"
          name="tenant_domain_key"
          type="text"
          required
        />
        <LabeledInput
          label="Project Number"
          placeholder="Enter project number"
          name="job_number"
          type="text"
          required
        />
        <Button type="submit">Check Project</Button>
      </form>

      {error && (
        <AlertNotification alertType="error" style={{marginTop: '10px'}}>
          {error}
        </AlertNotification>
      )}
      {info && (
        <AlertNotification alertType="warn" style={{marginTop: '10px'}}>
          {info}
        </AlertNotification>
      )}
      {success && (
        <AlertNotification alertType="success" style={{marginTop: '10px'}}>
          {success}
        </AlertNotification>
      )}

      {project && (
        <div style={{marginTop: '20px'}}>
          <div style={{marginBottom: '10px'}}>
            <strong>Tenant:</strong> {project.tenant_name} ({project.tenant_domain_key})
          </div>
          <div style={{marginBottom: '10px'}}>
            <strong>Project:</strong> #{project.job_number}
            {project.job_name ? ` — ${project.job_name}` : ''}
          </div>
          <div style={{marginBottom: '10px'}}>
            <strong>Tax mode:</strong> {taxModeLabel(project.tax_mode)}
          </div>
          <div style={{marginBottom: '10px'}}>
            <strong>Orders:</strong> {project.total_orders} total
            {project.zip2tax_orders > 0 ? ` (${project.zip2tax_orders} Zip2Tax)` : ''}
            {project.manual_orders > 0 ? ` (${project.manual_orders} manual)` : ''}
          </div>
          {canSwitch && (
            outOfSync ? (
              <div style={{display: 'flex', gap: '10px'}}>
                <Button type="button" onClick={() => handleSwitch('zip2tax')}>
                  Switch to Zip2Tax
                </Button>
                <Button type="button" onClick={() => handleSwitch('manual')}>
                  Switch to Manual taxes
                </Button>
              </div>
            ) : (
              <Button type="button" onClick={() => handleSwitch(oppositeMode)}>
                Switch to {taxModeLabel(oppositeMode)}
              </Button>
            )
          )}
        </div>
      )}
    </div>
  );
};

ProjectTaxMode.label = 'Project Tax Mode';

export default ProjectTaxMode;
